import type { GameState, CommandResult, LogEntry } from '../types/game';
import { getFishById } from './fish';
import { getLocationDefById, isLocationUnlocked, getUnmetRequirements, LOCATION_DEFS } from './locations';
import { getSkillDefById } from './skills';
import { getEquipmentDefById, getUpgradeCost, getBoatTierName } from './equipment';
import { calcBonus, getPlayerXpToNextLevel } from './skills';
import { validateGameState } from '../engine/save-schema';
import type { LocationDef } from '../types/game';
import { getBycatchById } from './bycatch';
import { getPropertyDefById, getPropertyUpgradeCost, getPropertyTierName, getHomeFlavorText, getDockFlavorText, DOCK_PURCHASE_COST } from './properties';
import { getNpcsAtLocation } from './npcs';
import { getVesselDefById, getVesselCap, VESSEL_DEFS, generateBoatName } from './vessels';
import { makeEntry, fmtCoins, spacer } from '../ui/format';

// ── Fish command ──

export function startFish(state: GameState): CommandResult {
  if (state.isFishing) {
    return { logEntries: [makeEntry('Already fishing! Wait or >cancel.', 'error')], statePatch: {} };
  }

  const location = getLocationDefById(state.currentLocation);
  if (!location || location.type !== 'fishing') {
    return { logEntries: [makeEntry('You can\'t fish here. Find a fishing spot.', 'error')], statePatch: {} };
  }

  return {
    logEntries: [makeEntry('You cast your line...', 'info')],
    statePatch: { isFishing: true, fishingStartTime: Date.now(), fishingGeneration: state.fishingGeneration + 1 },
  };
}

export function cancelFish(state: GameState): CommandResult {
  if (!state.isFishing) {
    return { logEntries: [makeEntry('You are not fishing.', 'error')], statePatch: {} };
  }
  return {
    logEntries: [makeEntry('You reel in your line. Nothing.', 'info')],
    statePatch: { isFishing: false, fishingStartTime: null },
  };
}

// ── Sell / Inventory ──

export function sell(state: GameState, args: string[]): CommandResult {
  const atMarket = state.currentLocation === 'market';
  const atHarbor = state.currentLocation === 'harbor';
  const target = args[0]?.toLowerCase();
  const isVesselOp = target === 'vessel' || target?.startsWith('vessel_');

  if (!atMarket && !atHarbor) {
    return { logEntries: [makeEntry('You need to be at the Harbor Market to sell fish, or Shipyard Harbor to sell vessels.', 'error')], statePatch: {} };
  }

  if (!isVesselOp && !atMarket) {
    return { logEntries: [makeEntry('You need to be at the Harbor Market to sell fish and items.', 'error')], statePatch: {} };
  }

  if (state.inventory.length === 0 && !isVesselOp) {
    return { logEntries: [makeEntry('Your inventory is empty.', 'error')], statePatch: {} };
  }

  const barteringSkill = state.playerStats.skills.bartering.level;
  const sellBonus = calcBonus(barteringSkill, 0.042);

  if (!target || target === 'all') {
    let totalValue = 0;
    const details: string[] = [];

    for (const item of state.inventory) {
      if (item.type === 'fish') {
        const species = getFishById(item.fishId);
        if (species) {
          const value = Math.round(item.weight * species.baseValue * sellBonus * 100) / 100;
          totalValue += value;
          details.push(`${species.name} (${item.weight}kg) = ${fmtCoins(value)} coins`);
        } else {
          details.push(`  (Unknown fish type: ${item.fishId} discarded)`);
        }
      } else {
        const bycatch = getBycatchById(item.bycatchId);
        if (bycatch) {
          const bcValue = Math.round(bycatch.value * sellBonus * 100) / 100;
          totalValue += bcValue;
          details.push(`${bycatch.name} (${bycatch.category}) = ${fmtCoins(bcValue)} coins`);
        } else {
          details.push(`  (Unknown item: ${item.bycatchId} discarded)`);
        }
      }
    }

    totalValue = Math.round(totalValue * 100) / 100;

    return {
      logEntries: [
        makeEntry(`Sold all items for ${fmtCoins(totalValue)} coins:`, 'success'),
        ...details.map(d => makeEntry(`  ${d}`, 'info')),
      ],
      statePatch: {
        coins: Math.round((state.coins + totalValue) * 100) / 100,
        totalCoinsEarned: Math.round((state.totalCoinsEarned + totalValue) * 100) / 100,
        inventory: [],
      },
    };
  }

  const species = getFishById(target);
  const bycatch = getBycatchById(target);

  if (species) {
    const matching = state.inventory.filter(i => i.type === 'fish' && i.fishId === target);
    if (matching.length === 0) {
      return { logEntries: [makeEntry(`You don't have any ${species.name}.`, 'error')], statePatch: {} };
    }

    let totalValue = 0;
    for (const item of matching) {
      if (item.type === 'fish') {
        totalValue += item.weight * species.baseValue * sellBonus;
      }
    }
    totalValue = Math.round(totalValue * 100) / 100;

    const newInventory = state.inventory.filter(i => !(i.type === 'fish' && i.fishId === target));
    return {
      logEntries: [makeEntry(`Sold ${matching.length}x ${species.name} for ${fmtCoins(totalValue)} coins.`, 'success')],
      statePatch: {
        coins: Math.round((state.coins + totalValue) * 100) / 100,
        totalCoinsEarned: Math.round((state.totalCoinsEarned + totalValue) * 100) / 100,
        inventory: newInventory,
      },
    };
  }

  if (bycatch) {
    const matching = state.inventory.filter(i => i.type === 'bycatch' && i.bycatchId === target);
    if (matching.length === 0) {
      return { logEntries: [makeEntry(`You don't have any ${bycatch.name}.`, 'error')], statePatch: {} };
    }

    const totalValue = Math.round(matching.length * bycatch.value * sellBonus * 100) / 100;
    const newInventory = state.inventory.filter(i => !(i.type === 'bycatch' && i.bycatchId === target));
    return {
      logEntries: [makeEntry(`Sold ${matching.length}x ${bycatch.name} for ${fmtCoins(totalValue)} coins.`, 'success')],
      statePatch: {
        coins: Math.round((state.coins + totalValue) * 100) / 100,
        totalCoinsEarned: Math.round((state.totalCoinsEarned + totalValue) * 100) / 100,
        inventory: newInventory,
      },
    };
  }

  if (target === 'vessel') {
    if (state.vessels.length === 0) {
      return { logEntries: [makeEntry('You don\'t own any vessels.', 'error')], statePatch: {} };
    }
    const logEntries: LogEntry[] = [
      spacer(),
      makeEntry('Your vessels (sell for 50% of purchase price):', 'system'),
    ];
    const byType = new Map<string, { def: ReturnType<typeof getVesselDefById>; vessels: typeof state.vessels }>();
    for (const v of state.vessels) {
      if (!byType.has(v.vesselId)) {
        byType.set(v.vesselId, { def: getVesselDefById(v.vesselId), vessels: [] });
      }
      byType.get(v.vesselId)!.vessels.push(v);
    }
    for (const [vid, group] of byType) {
      const sellPrice = Math.round((group.def?.cost ?? 0) * 0.5 * 100) / 100;
      for (const v of group.vessels) {
        logEntries.push(makeEntry(`  · ${v.name} (${group.def?.name || vid}) — >sell vessel_${vid} (${fmtCoins(sellPrice)} each)`, 'info'));
      }
    }
    return { logEntries, statePatch: {} };
  }

  if (target.startsWith('vessel_')) {
    const vesselId = target.slice(7);
    const vesselDef = getVesselDefById(vesselId);
    if (!vesselDef) {
      return { logEntries: [makeEntry(`Unknown vessel type: ${vesselId}.`, 'error')], statePatch: {} };
    }
    const matching = state.vessels.filter(v => v.vesselId === vesselId);
    if (matching.length === 0) {
      return { logEntries: [makeEntry(`You don't own any ${vesselDef.name}s.`, 'error')], statePatch: {} };
    }
    const soldVessel = matching[0];
    const sellPrice = Math.round(vesselDef.cost * 0.5 * 100) / 100;
    const newVessels = state.vessels.filter(v => v !== soldVessel);
    return {
      logEntries: [makeEntry(`Sold "${soldVessel.name}" (${vesselDef.name}) for ${fmtCoins(sellPrice)} coins.`, 'success')],
      statePatch: {
        coins: Math.round((state.coins + sellPrice) * 100) / 100,
        totalCoinsEarned: Math.round((state.totalCoinsEarned + sellPrice) * 100) / 100,
        vessels: newVessels,
      },
    };
  }

  return {
    logEntries: [makeEntry(`Unknown item: ${target}. Use >inventory to see what you have.`, 'error')],
    statePatch: {},
  };
}

export function showInventory(state: GameState): CommandResult {
  if (state.inventory.length === 0) {
    return { logEntries: [spacer(), makeEntry('Your inventory is empty.', 'info')], statePatch: {} };
  }

  const barteringSkill = state.playerStats.skills.bartering.level;
  const sellBonus = calcBonus(barteringSkill, 0.042);

  const fishItems = state.inventory.filter(i => i.type === 'fish');
  const bycatchItems = state.inventory.filter(i => i.type === 'bycatch');
  const logEntries: LogEntry[] = [spacer(), makeEntry('Inventory:', 'system')];

  if (fishItems.length > 0) {
    const fishCounts = new Map<string, { count: number; weight: number }>();
    for (const item of fishItems) {
      if (item.type !== 'fish') continue;
      const existing = fishCounts.get(item.fishId) || { count: 0, weight: 0 };
      existing.count++;
      existing.weight += item.weight;
      fishCounts.set(item.fishId, existing);
    }

    logEntries.push(makeEntry('  🐟 Fish:', 'info'));
    for (const [fishId, data] of fishCounts) {
      const species = getFishById(fishId);
      const name = species?.name || fishId;
      const totalValue = Math.round(data.weight * (species?.baseValue || 0) * sellBonus * 100) / 100;
      logEntries.push(
        makeEntry(`    · ${name}: ${data.count}x, ${Math.round(data.weight * 100) / 100}kg (~${fmtCoins(totalValue)} coins)`, 'info'),
      );
    }
  }

  if (bycatchItems.length > 0) {
    const bycatchCounts = new Map<string, number>();
    for (const item of bycatchItems) {
      if (item.type !== 'bycatch') continue;
      bycatchCounts.set(item.bycatchId, (bycatchCounts.get(item.bycatchId) || 0) + 1);
    }

    logEntries.push(spacer(), makeEntry('  📦 Bycatch:', 'info'));
    for (const [bycatchId, count] of bycatchCounts) {
      const def = getBycatchById(bycatchId);
      if (def) {
        const bcValue = Math.round(def.value * sellBonus * 100) / 100;
        logEntries.push(
          makeEntry(`    · ${def.name} (${def.category}): ${count}x, ${fmtCoins(bcValue)} coin${bcValue !== 1 ? 's' : ''} each`, 'info'),
        );
      }
    }
  }

  return { logEntries, statePatch: {} };
}

export function buy(state: GameState, args: string[]): CommandResult {
  if (state.currentLocation !== 'harbor') {
    return { logEntries: [makeEntry('You need to be at the Shipyard Harbor to buy vessels.', 'error')], statePatch: {} };
  }

  const dockProp = state.properties.find(p => p.id === 'dock');
  const dockTier = dockProp?.tier ?? 0;
  const businessLevel = state.playerStats.skills.business.level;
  const cap = getVesselCap(businessLevel, dockTier);
  const totalOwned = state.vessels.length;

  if (args.length === 0) {
    const activeTrips = state.vesselTrips.length;
    const dockName = dockProp ? getPropertyTierName('dock', dockTier) : 'no dock';
    const logEntries: LogEntry[] = [
      spacer(),
      makeEntry('═══ Shipyard ═══', 'system'),
      makeEntry(`You own ${totalOwned}/${cap} vessels (${activeTrips} on trips).`, 'info'),
      makeEntry(`Vessel cap: ${cap} (2 base + ${Math.floor(businessLevel / 10)} from Business Lv.${businessLevel}${dockTier > 0 ? ` + ${dockTier} from ${dockName}` : ''})`, 'info'),
      spacer(),
    ];

    if (!dockProp) {
      logEntries.push(
        makeEntry(`  · Build a Private Dock — ${fmtCoins(DOCK_PURCHASE_COST)} coins — >buy dock`, 'item'),
        makeEntry('    A private berth. +1 vessel cap per tier. Unlocks fleet management.', 'info'),
        spacer(),
      );
    }

    for (const vdef of VESSEL_DEFS) {
      logEntries.push(makeEntry(`  · ${vdef.name} — ${fmtCoins(vdef.cost)} coins — >buy ${vdef.id}`, 'item'));
      logEntries.push(makeEntry(`    ${vdef.description}`, 'info'));
      logEntries.push(makeEntry(`    Trip: ~${vdef.tripDurationSeconds}s, ~${fmtCoins(vdef.coinsPerSecond)}/s avg`, 'info'));
      logEntries.push(spacer());
    }
    logEntries.push(makeEntry('Use >buy <vesselId> to purchase. Visit your dock to rename vessels.', 'system'));
    return { logEntries, statePatch: {} };
  }

  const arg = args[0].toLowerCase();

  if (arg === 'dock') {
    if (dockProp) {
      return { logEntries: [makeEntry('You already own a dock. Use >upgrade dock at your dock to improve it.', 'error')], statePatch: {} };
    }
    if (state.coins < DOCK_PURCHASE_COST) {
      return { logEntries: [makeEntry(`Not enough coins. Need ${fmtCoins(DOCK_PURCHASE_COST)}, have ${fmtCoins(state.coins)}.`, 'error')], statePatch: {} };
    }
    return {
      logEntries: [
        makeEntry(`Built a Wooden Pier for ${fmtCoins(DOCK_PURCHASE_COST)} coins! Your dock is now accessible. +1 vessel cap.`, 'success'),
      ],
      statePatch: {
        coins: Math.round((state.coins - DOCK_PURCHASE_COST) * 100) / 100,
        properties: [...state.properties, { id: 'dock', tier: 1 }],
        discoveredLocations: [...state.discoveredLocations, 'dock'],
      },
    };
  }

  const vesselDef = getVesselDefById(arg);

  if (!vesselDef) {
    return { logEntries: [makeEntry(`Unknown vessel: ${arg}. Use >buy to see available vessels.`, 'error')], statePatch: {} };
  }

  if (totalOwned >= cap) {
    const dockName = dockProp ? getPropertyTierName('dock', dockTier) : 'no dock';
    return { logEntries: [makeEntry(`Vessel cap reached (${cap}). Sell a vessel first (>sell vessel_<id>), build/upgrade your dock (${dockName}), or increase Business skill (Lv.${businessLevel}).`, 'error')], statePatch: {} };
  }

  if (state.coins < vesselDef.cost) {
    return { logEntries: [makeEntry(`Not enough coins. Need ${fmtCoins(vesselDef.cost)}, have ${fmtCoins(state.coins)}.`, 'error')], statePatch: {} };
  }

  const boatName = generateBoatName();

  return {
    logEntries: [makeEntry(`Purchased a ${vesselDef.name} named "${boatName}" for ${fmtCoins(vesselDef.cost)} coins! It will start fishing trips automatically.`, 'success')],
    statePatch: {
      coins: Math.round((state.coins - vesselDef.cost) * 100) / 100,
      vessels: [...state.vessels, { vesselId: arg, name: boatName }],
    },
  };
}

// ── Fleet / Rename ──

export function showFleet(state: GameState): CommandResult {
  if (state.currentLocation !== 'dock') {
    return { logEntries: [makeEntry('You need to be at your dock to manage your fleet.', 'error')], statePatch: {} };
  }

  if (state.vessels.length === 0) {
    return { logEntries: [makeEntry('You don\'t own any vessels. Visit the Shipyard Harbor to buy one.', 'info')], statePatch: {} };
  }

  const dockProp = state.properties.find(p => p.id === 'dock');
  const dockTier = dockProp?.tier ?? 0;
  const dockName = dockProp ? getPropertyTierName('dock', dockTier) : 'no dock';
  const businessLevel = state.playerStats.skills.business.level;
  const cap = getVesselCap(businessLevel, dockTier);

  const logEntries: LogEntry[] = [
    spacer(),
    makeEntry('═══ Your Fleet ═══', 'system'),
    makeEntry(`${dockName} — ${state.vessels.length}/${cap} berths filled`, 'info'),
    spacer(),
  ];

  const byType = new Map<string, { def: ReturnType<typeof getVesselDefById>; vessels: typeof state.vessels }>();
  for (const v of state.vessels) {
    if (!byType.has(v.vesselId)) {
      byType.set(v.vesselId, { def: getVesselDefById(v.vesselId), vessels: [] });
    }
    byType.get(v.vesselId)!.vessels.push(v);
  }

  for (const [vid, group] of byType) {
    const typeName = group.def?.name || vid;
    for (const v of group.vessels) {
      const onTrip = state.vesselTrips.some(t => t.name === v.name);
      const status = onTrip ? '⏳ On trip' : '⚓ Docked';
      logEntries.push(makeEntry(`  · ${v.name} (${typeName}) — ${status}`, onTrip ? 'info' : 'item'));
    }
  }

  logEntries.push(
    spacer(),
    makeEntry('Use >rename <currentName> <newName> to rename a vessel.', 'system'),
  );
  return { logEntries, statePatch: {} };
}

export function renameVessel(state: GameState, args: string[]): CommandResult {
  if (state.currentLocation !== 'dock') {
    return { logEntries: [makeEntry('You need to be at your dock to rename vessels.', 'error')], statePatch: {} };
  }

  if (args.length < 2) {
    return { logEntries: [makeEntry('Usage: rename <currentName> <newName>', 'error')], statePatch: {} };
  }

  const currentName = args[0];
  const newName = args.slice(1).join(' ');

  if (newName.length > 30) {
    return { logEntries: [makeEntry('Name too long. Max 30 characters.', 'error')], statePatch: {} };
  }

  const vessel = state.vessels.find(v => v.name.toLowerCase() === currentName.toLowerCase());
  if (!vessel) {
    return { logEntries: [makeEntry(`No vessel named "${currentName}". Use >fleet to see your vessels.`, 'error')], statePatch: {} };
  }

  if (state.vessels.some(v => v.name.toLowerCase() === newName.toLowerCase())) {
    return { logEntries: [makeEntry(`A vessel named "${newName}" already exists.`, 'error')], statePatch: {} };
  }

  const def = getVesselDefById(vessel.vesselId);
  const newVessels = state.vessels.map(v =>
    v.name.toLowerCase() === currentName.toLowerCase() ? { ...v, name: newName } : v
  );

  return {
    logEntries: [makeEntry(`Renamed "${currentName}" to "${newName}" (${def?.name || vessel.vesselId}).`, 'success')],
    statePatch: { vessels: newVessels },
  };
}

// ── Status / Shop ──

export function showStatus(state: GameState): CommandResult {
  const location = getLocationDefById(state.currentLocation);
  const ps = state.playerStats;
  const logEntries: LogEntry[] = [
    makeEntry('', 'info'),
    makeEntry('═══ Character ═══', 'system'),
    makeEntry(`  Level: ${ps.level} (${ps.xp.toLocaleString()} / ${ps.xpToNext.toLocaleString()} XP)`, 'info'),
    makeEntry(`  Unallocated Points: ${ps.unallocatedPoints}`, ps.unallocatedPoints > 0 ? 'success' : 'info'),
    spacer(),
    makeEntry('═══ Skills ═══', 'system'),
  ];

  for (const skillId of ['fishing', 'bartering', 'business', 'luck'] as const) {
    const skill = ps.skills[skillId];
    const def = getSkillDefById(skillId);
    logEntries.push(makeEntry(`  · ${def?.name || skillId}: Lv.${skill.level}`, 'info'));
  }

  logEntries.push(spacer(), makeEntry('═══ Equipment ═══', 'system'));

  for (const equip of state.equipment) {
    const def = getEquipmentDefById(equip.id);
    if (equip.id === 'boat') {
      const boatUpgradeCost = equip.level < 5 ? getUpgradeCost('boat', equip.level) : 0;
      logEntries.push(
        makeEntry(`  · ${def?.name || equip.id}: ${getBoatTierName(equip.level)}${equip.level < 5 ? ` — >upgrade boat (${fmtCoins(boatUpgradeCost)})` : ' (Max)'}`, 'info'),
      );
    } else {
      const upgradeCost = getUpgradeCost(equip.id, equip.level);
      logEntries.push(
        makeEntry(`  · ${def?.name || equip.id}: Lv.${equip.level} — >upgrade ${equip.id} (${fmtCoins(upgradeCost)})`, 'info'),
      );
    }
  }

  if (state.properties.length > 0) {
    logEntries.push(spacer(), makeEntry('═══ Properties ═══', 'system'));
    for (const prop of state.properties) {
      const def = getPropertyDefById(prop.id);
      const upgradeCost = getPropertyUpgradeCost(prop.id, prop.tier);
      const tierName = getPropertyTierName(prop.id, prop.tier);
      if (upgradeCost !== null) {
        logEntries.push(makeEntry(`  · ${def?.name || prop.id}: ${tierName} — >upgrade ${prop.id} (${fmtCoins(upgradeCost)})`, 'info'));
      } else {
        logEntries.push(makeEntry(`  · ${def?.name || prop.id}: ${tierName} (Max)`, 'info'));
      }
    }
  }

  logEntries.push(
    spacer(),
    makeEntry('═══ Finances ═══', 'system'),
    makeEntry(`  Coins: ${fmtCoins(state.coins)}`, 'info'),
    makeEntry(`  Total earned: ${fmtCoins(state.totalCoinsEarned)}`, 'info'),
  );

  const totalVessels = state.vessels.length;
  const activeTrips = state.vesselTrips.length;
  const dockProp = state.properties.find(p => p.id === 'dock');
  const dockTier = dockProp?.tier ?? 0;
  const vesselCap = getVesselCap(state.playerStats.skills.business.level, dockTier);
  if (totalVessels > 0) {
    logEntries.push(
      spacer(),
      makeEntry('═══ Fleet ═══', 'system'),
      makeEntry(`  Berths: ${totalVessels}/${vesselCap} (${activeTrips} on fishing trips)`, 'info'),
    );
    const byType = new Map<string, { def: ReturnType<typeof getVesselDefById>; count: number; busy: number }>();
    for (const v of state.vessels) {
      if (!byType.has(v.vesselId)) {
        byType.set(v.vesselId, { def: getVesselDefById(v.vesselId), count: 0, busy: 0 });
      }
      const entry = byType.get(v.vesselId)!;
      entry.count++;
      if (state.vesselTrips.some(t => t.name === v.name)) entry.busy++;
    }
    for (const [vid, group] of byType) {
      logEntries.push(makeEntry(`  · ${group.def?.name || vid}: ${group.count}x (${group.busy} out, ${group.count - group.busy} docked)`, 'item'));
    }
  }

  logEntries.push(
    spacer(),
    makeEntry('═══ Progress ═══', 'system'),
    makeEntry(`  Location: ${location?.name || state.currentLocation}`, 'info'),
    makeEntry(`  Fish caught: ${state.fishCaught}`, 'info'),
    makeEntry(`  Inventory: ${state.inventory.length} items`, 'info'),
    makeEntry(`  Locations: ${state.discoveredLocations.length}/${LOCATION_DEFS.length}`, 'info'),
  );

  return { logEntries, statePatch: {} };
}

export function showShop(state: GameState): CommandResult {
  if (state.currentLocation !== 'market') {
    return { logEntries: [makeEntry('You need to be at the Harbor Market to browse the shop.', 'error')], statePatch: {} };
  }

  return {
    logEntries: [
      spacer(),
      makeEntry('═══ Shop ═══', 'system'),
      makeEntry('Equipment upgrades: rod, boat, bait, line', 'info'),
      makeEntry('Property upgrades: visit your home and use >upgrade home', 'info'),
      spacer(),
      makeEntry('Use >upgrade <id> to upgrade your equipment.', 'system'),
    ],
    statePatch: {},
  };
}

// ── Unlock notifications ──

export type UnlockEntry = {
  id: string;
  name: string;
  category: 'location' | 'npc' | 'tool' | 'equipment';
  description?: string;
};

export function buildUnlockNotifications(unlocks: UnlockEntry[]): LogEntry[] {
  if (unlocks.length === 0) return [];

  const byCategory = new Map<string, UnlockEntry[]>();
  for (const u of unlocks) {
    const list = byCategory.get(u.category) || [];
    list.push(u);
    byCategory.set(u.category, list);
  }

  const categoryIcons: Record<string, string> = {
    location: '📍', npc: '👤', tool: '🔧', equipment: '⚙️',
  };
  const categoryLabels: Record<string, string> = {
    location: 'New location discovered', npc: 'New character met', tool: 'New tool unlocked', equipment: 'New equipment available',
  };

  const entries: LogEntry[] = [];
  for (const [category, items] of byCategory) {
    const icon = categoryIcons[category] || '✨';
    const label = categoryLabels[category] || 'New unlock';
    if (items.length === 1) {
      entries.push(makeEntry(`${icon} ${label}: ${items[0].name}`, 'success'));
    } else {
      const names = items.map(i => i.name).join(', ');
      entries.push(makeEntry(`${icon} ${label}: ${names}`, 'success'));
    }
  }

  return entries;
}

// ── Travel / Look / Map ──

export function travel(state: GameState, args: string[]): CommandResult {
  if (args.length === 0) {
    return { logEntries: [makeEntry('Usage: go <locationId>. Use >look to see available destinations.', 'error')], statePatch: {} };
  }

  const targetId = args[0].toLowerCase();
  const targetDef = getLocationDefById(targetId);

  if (!targetDef) {
    return { logEntries: [makeEntry(`Unknown location: ${targetId}.`, 'error')], statePatch: {} };
  }

  if (!isLocationUnlocked(targetDef, state)) {
    const unmet = getUnmetRequirements(targetDef, state);
    if (unmet.length > 0) {
      return { logEntries: [makeEntry(`Requirements not met: ${unmet.join(', ')}`, 'error')], statePatch: {} };
    }
    return { logEntries: [makeEntry(`${targetDef.name} is locked.`, 'error')], statePatch: {} };
  }

  if (targetId === state.currentLocation) {
    return { logEntries: [makeEntry(`You are already at ${targetDef.name}.`, 'info')], statePatch: {} };
  }

  const patch: Partial<GameState> = { currentLocation: targetId };
  const logEntries: LogEntry[] = [
    makeEntry(`You travel to ${targetDef.name}.`, 'system'),
    makeEntry(targetDef.description, 'info'),
  ];

  const newDiscoveries: string[] = [];
  const unlockEntries: UnlockEntry[] = [];
  if (!state.discoveredLocations.includes(targetId)) {
    newDiscoveries.push(targetId);
    unlockEntries.push({ id: targetId, name: targetDef.name, category: 'location' });
  }
  for (const revealedId of targetDef.reveals) {
    if (!state.discoveredLocations.includes(revealedId)) {
      const revealedDef = getLocationDefById(revealedId);
      if (revealedDef && isLocationUnlocked(revealedDef, state)) {
        newDiscoveries.push(revealedId);
        unlockEntries.push({ id: revealedId, name: revealedDef.name, category: 'location' });
      }
    }
  }

  if (unlockEntries.length > 0) {
    logEntries.push(...buildUnlockNotifications(unlockEntries));
  }

  if (newDiscoveries.length > 0) {
    patch.discoveredLocations = [...state.discoveredLocations, ...newDiscoveries];
  }

  return { logEntries, statePatch: patch, transitTo: targetId };
}

function buildPlaceHeader(state: GameState, location: LocationDef): LogEntry[] {
  let description = location.description;
  if (location.id === 'home') {
    const homeProp = state.properties.find(p => p.id === 'home');
    const tier = homeProp?.tier ?? 1;
    description = getHomeFlavorText(tier);
  }
  return [
    makeEntry(`═══ ${location.name} ═══`, 'location'),
    makeEntry(description, 'info'),
    spacer(),
  ];
}

function buildLocationHint(state: GameState, location: LocationDef): LogEntry[] {
  if (location.type === 'fishing') {
    return [makeEntry('🎣 You can fish here.', 'info'), spacer()];
  }
  if (location.type === 'market') {
    return [makeEntry('🏪 Sell your catch and upgrade equipment here.', 'info'), spacer()];
  }
  if (location.type === 'harbor') {
    const dockProp = state.properties.find(p => p.id === 'dock');
    const dockMsg = dockProp ? 'Upgrade vessels and your dock.' : `Build a dock for ${fmtCoins(DOCK_PURCHASE_COST)} coins (>buy dock).`;
    return [makeEntry(`⚓ Buy and sell vessels for your fishing fleet. ${dockMsg}`, 'info'), spacer()];
  }
  if (location.type === 'dock') {
    const dockProp = state.properties.find(p => p.id === 'dock');
    const tier = dockProp?.tier ?? 1;
    const upgradeCost = getPropertyUpgradeCost('dock', tier);
    const hint = upgradeCost !== null
      ? `⚓ ${getDockFlavorText(tier)} — >upgrade dock (${fmtCoins(upgradeCost)} coins, gives +1 vessel cap)`
      : `⚓ ${getDockFlavorText(tier)} — fully upgraded!`;
    return [makeEntry(hint, 'info'), spacer()];
  }
  if (location.type === 'house') {
    const homeProp = state.properties.find(p => p.id === 'home');
    const tier = homeProp?.tier ?? 1;
    const tierName = getPropertyTierName('home', tier);
    const upgradeCost = getPropertyUpgradeCost('home', tier);
    const hint = upgradeCost !== null
      ? `🏠 Your ${tierName} — >upgrade home to improve (${fmtCoins(upgradeCost)} coins)`
      : `🏠 Your ${tierName} — fully upgraded!`;
    return [makeEntry(hint, 'info'), spacer()];
  }
  return [];
}

function buildAvailableCommands(location: LocationDef): LogEntry[] {
  const commands: string[] = ['>inventory', '>status'];
  if (location.type === 'fishing') commands.unshift('>fish');
  if (location.type === 'market') {
    commands.unshift('>sell', '>upgrade', '>equipment');
  }
  if (location.type === 'harbor') {
    commands.unshift('>buy', '>sell');
  }
  if (location.type === 'dock') {
    commands.unshift('>fleet', '>rename');
  }
  return [makeEntry(`📦 Commands: ${commands.join(', ')}`, 'command'), spacer()];
}

function buildNpcs(locationId: string): LogEntry[] {
  const npcs = getNpcsAtLocation(locationId);
  if (npcs.length === 0) return [];
  const entries: LogEntry[] = [makeEntry('👤 Here:', 'npc')];
  for (const npc of npcs) {
    entries.push(makeEntry(`  · ${npc}`, 'npc'));
  }
  entries.push(spacer());
  return entries;
}

export function look(state: GameState): CommandResult {
  const location = getLocationDefById(state.currentLocation);
  if (!location) {
    return { logEntries: [makeEntry('You are nowhere. This shouldn\'t happen.', 'error')], statePatch: {} };
  }

  return {
    logEntries: [
      ...buildPlaceHeader(state, location),
      ...buildLocationHint(state, location),
      ...buildAvailableCommands(location),
      ...buildNpcs(state.currentLocation),
    ],
    statePatch: {},
  };
}

export function showMap(state: GameState): CommandResult {
  const logEntries: LogEntry[] = [
    spacer(),
    makeEntry('═══ Map ═══', 'system'),
  ];

  const unlocked = LOCATION_DEFS.filter(l => isLocationUnlocked(l, state));
  const locked = LOCATION_DEFS.filter(l => !isLocationUnlocked(l, state));

  if (unlocked.length > 0) {
    logEntries.push(makeEntry('Known locations:', 'system'));
    for (const loc of unlocked) {
      const here = loc.id === state.currentLocation ? ' <-- You are here' : '';
      const visited = state.discoveredLocations.includes(loc.id) ? '' : ' (unvisited)';
      logEntries.push(
        makeEntry(`  · ${loc.name}${visited}${here} — >go ${loc.id}`, loc.id === state.currentLocation ? 'success' : 'info'),
      );
    }
  }

  const revealedLocked = locked.filter(loc =>
    state.discoveredLocations.some(discId => {
      const disc = getLocationDefById(discId);
      return disc?.reveals.includes(loc.id);
    })
  );

  if (revealedLocked.length > 0) {
    logEntries.push(spacer(), makeEntry('Locked:', 'system'));
    for (const loc of revealedLocked) {
      const unmet = getUnmetRequirements(loc, state);
      if (unmet.length > 0) {
        logEntries.push(makeEntry(`  · ${loc.name}: ${unmet.join(', ')}`, 'info'));
      } else {
        logEntries.push(makeEntry(`  · ??? (locked)`, 'info'));
      }
    }
  }

  return { logEntries, statePatch: {} };
}

// ── Skill / Upgrade / Equipment / Properties ──

export function allocateSkill(state: GameState, args: string[]): CommandResult {
  if (args.length === 0) {
    return { logEntries: [makeEntry('Usage: allocate <skillId>. Skills: fishing, bartering, business, luck', 'error')], statePatch: {} };
  }

  const skillId = args[0].toLowerCase();
  const validSkills = ['fishing', 'bartering', 'business', 'luck'];
  if (!validSkills.includes(skillId)) {
    return { logEntries: [makeEntry(`Unknown skill: ${skillId}. Valid: ${validSkills.join(', ')}`, 'error')], statePatch: {} };
  }

  if (state.playerStats.unallocatedPoints <= 0) {
    return { logEntries: [makeEntry('No unallocated stat points. Level up to earn more.', 'error')], statePatch: {} };
  }

  const newLevel = state.playerStats.skills[skillId as keyof typeof state.playerStats.skills].level + 1;

  return {
    logEntries: [
      makeEntry(`${skillId.charAt(0).toUpperCase() + skillId.slice(1)} increased to level ${newLevel}!`, 'success'),
    ],
    statePatch: {
      playerStats: {
        ...state.playerStats,
        unallocatedPoints: state.playerStats.unallocatedPoints - 1,
        skills: {
          ...state.playerStats.skills,
          [skillId]: { level: newLevel },
        },
      },
    },
  };
}

export function upgradeEquipment(state: GameState, args: string[]): CommandResult {
  if (args.length === 0) {
    return { logEntries: [makeEntry('Usage: upgrade <equipmentId>. Equipment: rod, boat, bait, line', 'error')], statePatch: {} };
  }

  const equipId = args[0].toLowerCase();

  if (state.currentLocation !== 'market') {
    return { logEntries: [makeEntry('You need to be at the Harbor Market to upgrade equipment.', 'error')], statePatch: {} };
  }

  const equip = state.equipment.find(e => e.id === equipId);
  if (!equip) {
    return { logEntries: [makeEntry(`Unknown equipment: ${equipId}. Valid: rod, boat, bait, line`, 'error')], statePatch: {} };
  }

  if (equipId === 'boat' && equip.level >= 5) {
    return { logEntries: [makeEntry('Your boat is already fully upgraded (Frigate).', 'info')], statePatch: {} };
  }

  const cost = getUpgradeCost(equipId, equip.level);
  if (state.coins < cost) {
    return { logEntries: [makeEntry(`Not enough coins. Need ${fmtCoins(cost)}, have ${fmtCoins(state.coins)}.`, 'error')], statePatch: {} };
  }

  const def = getEquipmentDefById(equipId);
  const newLevel = equip.level + 1;

  const upgradeMsg = equipId === 'boat'
    ? `${def?.name || equipId} upgraded to ${getBoatTierName(newLevel)} for ${fmtCoins(cost)} coins!`
    : `${def?.name || equipId} upgraded to level ${newLevel} for ${fmtCoins(cost)} coins!`;

  return {
    logEntries: [makeEntry(upgradeMsg, 'success')],
    statePatch: {
      coins: Math.round((state.coins - cost) * 100) / 100,
      equipment: state.equipment.map(e => e.id === equipId ? { ...e, level: newLevel } : e),
    },
  };
}

export function upgradeProperty(state: GameState, args: string[]): CommandResult {
  const propId = args[0]?.toLowerCase() || 'home';

  if (propId === 'home' && state.currentLocation !== 'home') {
    return { logEntries: [makeEntry('You need to be at home to upgrade your home.', 'error')], statePatch: {} };
  }
  if (propId === 'dock' && state.currentLocation !== 'dock') {
    return { logEntries: [makeEntry('You need to be at your dock to upgrade it.', 'error')], statePatch: {} };
  }

  const prop = state.properties.find(p => p.id === propId);
  if (!prop) {
    return { logEntries: [makeEntry(`Unknown property: ${propId}.`, 'error')], statePatch: {} };
  }

  if (prop.tier >= 5) {
    return { logEntries: [makeEntry(`Your ${propId} is already fully upgraded.`, 'info')], statePatch: {} };
  }

  const cost = getPropertyUpgradeCost(propId, prop.tier);
  if (cost === null) {
    return { logEntries: [makeEntry(`Your ${propId} is already fully upgraded.`, 'info')], statePatch: {} };
  }

  if (state.coins < cost) {
    return { logEntries: [makeEntry(`Not enough coins. Need ${fmtCoins(cost)}, have ${fmtCoins(state.coins)}.`, 'error')], statePatch: {} };
  }

  const newTier = prop.tier + 1;
  const newTierName = getPropertyTierName(propId, newTier);

  return {
    logEntries: [
      makeEntry(`Your ${propId} has been upgraded to ${newTierName} for ${fmtCoins(cost)} coins!`, 'success'),
    ],
    statePatch: {
      coins: Math.round((state.coins - cost) * 100) / 100,
      properties: state.properties.map(p => p.id === propId ? { ...p, tier: newTier } : p),
    },
  };
}

export function showEquipment(state: GameState): CommandResult {
  const logEntries: LogEntry[] = [
    spacer(),
    makeEntry('═══ Equipment ═══', 'system'),
  ];

  for (const equip of state.equipment) {
    const def = getEquipmentDefById(equip.id);
    if (equip.id === 'boat') {
      const upgradeCost = equip.level < 5 ? getUpgradeCost('boat', equip.level) : null;
      logEntries.push(
        makeEntry(`  · ${def?.name || equip.id}: ${getBoatTierName(equip.level)} (Tier ${equip.level}/5)`, 'item'),
        makeEntry(`    ${def?.description || ''}`, 'info'),
        upgradeCost !== null
          ? makeEntry(`    Upgrade cost: ${fmtCoins(upgradeCost)} coins — >upgrade ${equip.id}`, 'info')
          : makeEntry(`    Max tier reached.`, 'info'),
        spacer(),
      );
    } else {
      const upgradeCost = getUpgradeCost(equip.id, equip.level);
      logEntries.push(
        makeEntry(`  · ${def?.name || equip.id}: Lv.${equip.level}`, 'item'),
        makeEntry(`    ${def?.description || ''}`, 'info'),
        makeEntry(`    Upgrade cost: ${fmtCoins(upgradeCost)} coins — >upgrade ${equip.id}`, 'info'),
        spacer(),
      );
    }
  }

  return { logEntries, statePatch: {} };
}

export function showProperties(state: GameState): CommandResult {
  if (state.properties.length === 0) {
    return { logEntries: [makeEntry('You don\'t own any properties.', 'info')], statePatch: {} };
  }

  const logEntries: LogEntry[] = [
    spacer(),
    makeEntry('═══ Properties ═══', 'system'),
  ];

  for (const prop of state.properties) {
    const def = getPropertyDefById(prop.id);
    const tierName = getPropertyTierName(prop.id, prop.tier);
    const upgradeCost = getPropertyUpgradeCost(prop.id, prop.tier);
    logEntries.push(
      makeEntry(`  · ${def?.name || prop.id}: ${tierName} (Tier ${prop.tier}/5)`, 'item'),
      makeEntry(`    ${def?.description || ''}`, 'info'),
    );
    if (upgradeCost !== null) {
      logEntries.push(
        makeEntry(`    Upgrade cost: ${fmtCoins(upgradeCost)} coins — >upgrade ${prop.id}`, 'info'),
      );
    } else {
      logEntries.push(makeEntry(`    Max tier reached.`, 'info'));
    }
    logEntries.push(spacer());
  }

  return { logEntries, statePatch: {} };
}

// ── Help / Tutorial / Achievements ──

export function showHelp(_state: GameState): CommandResult {
  return {
    logEntries: [
      spacer(),
      makeEntry('═══ 🧭 Movement ═══', 'system'),
      makeEntry('  >look, >l        — Look around', 'command'),
      makeEntry('  >go <place>      — Travel to a place', 'command'),
      makeEntry('  >map             — View all locations', 'command'),
      spacer(),
      makeEntry('═══ 🎣 Fishing ═══', 'system'),
      makeEntry('  >fish, >f       — Cast your line', 'command'),
      makeEntry('  >cancel          — Reel in early', 'command'),
      spacer(),
      makeEntry('═══ 🏪 Harbor Market ═══', 'system'),
      makeEntry('  >sell all        — Sell all fish and items', 'command'),
      makeEntry('  >sell <id>       — Sell specific fish or item', 'command'),
      makeEntry('  >upgrade <id>    — Upgrade equipment', 'command'),
      makeEntry('  >equipment       — View equipment details', 'command'),
      spacer(),
      makeEntry('═══ ⚓ Shipyard Harbor ═══', 'system'),
      makeEntry('  >buy             — View available vessels', 'command'),
      makeEntry('  >buy <id>        — Purchase a vessel', 'command'),
      makeEntry('  >sell vessel_<id> — Sell a vessel (at harbor)', 'command'),
      spacer(),
      makeEntry('═══ 🏠 Home ═══', 'system'),
      makeEntry('  >upgrade home    — Upgrade your house (while at home)', 'command'),
      spacer(),
      makeEntry('═══ ⚓ Your Dock ═══', 'system'),
      makeEntry('  >fleet           — View your fleet', 'command'),
      makeEntry('  >rename <old> <new> — Rename a vessel', 'command'),
      makeEntry('  >upgrade dock    — Upgrade dock (more vessel berths)', 'command'),
      spacer(),
      makeEntry('═══ 👤 Character ═══', 'system'),
      makeEntry('  >status, >s      — View detailed status', 'command'),
      makeEntry('  >allocate <skill> — Allocate a stat point', 'command'),
      makeEntry('  >achievements    — View milestones', 'command'),
      spacer(),
      makeEntry('═══ 📋 General ═══', 'system'),
      makeEntry('  >inventory, >i   — Check your catches', 'command'),
      makeEntry('  >tutorial        — Quick start guide', 'command'),
      makeEntry('  >export          — Export save', 'command'),
      makeEntry('  >import <code>   — Import save', 'command'),
      makeEntry('  >clear           — Clear terminal', 'command'),
      makeEntry('  >reset           — Reset all progress', 'command'),
      makeEntry('  >help            — Show this help', 'command'),
    ],
    statePatch: {},
  };
}

export function showTutorial(_state: GameState): CommandResult {
  return {
    logEntries: [
      spacer(),
      makeEntry('═══ Quick Start Guide ═══', 'system'),
      spacer(),
      makeEntry('1. You start at your cabin. Type >look to see where you can go.', 'info'),
      makeEntry('2. Type >go pond to travel to the Village Pond.', 'info'),
      makeEntry('3. Type >fish to cast your line. Wait for a bite!', 'info'),
      makeEntry('4. Type >go market to visit the Harbor Market.', 'info'),
      makeEntry('5. Type >sell all to sell your catch for coins.', 'info'),
      makeEntry('6. Type >upgrade rod to improve your fishing gear.', 'info'),
      makeEntry('7. Type >go harbor to visit the Shipyard and buy vessels.', 'info'),
      makeEntry('8. Type >go dock to manage your fleet and rename boats.', 'info'),
      makeEntry('9. Upgrade your dock for more vessel berths, and your home for comfort.', 'info'),
      spacer(),
      makeEntry('Tip: Use >allocate to distribute stat points when you level up.', 'system'),
      makeEntry('Tip: Upgrade equipment at the market, properties at home/dock.', 'system'),
    ],
    statePatch: {},
  };
}

// ── Debug / Export / Import ──

export function debugCommand(state: GameState, args: string[]): CommandResult {
  if (args.length === 0) {
    return {
      logEntries: [makeEntry('Debug subcommands: coins <amount>, skill <id> <level>, level <level>', 'system')],
      statePatch: {},
    };
  }

  const sub = args[0].toLowerCase();

  if (sub === 'coins') {
    const amount = parseFloat(args[1]);
    if (isNaN(amount)) {
      return { logEntries: [makeEntry('Usage: debug coins <amount>', 'error')], statePatch: {} };
    }
    return { logEntries: [makeEntry(`Coins set to ${amount.toFixed(2)}.`, 'system')], statePatch: { coins: amount } };
  }

  if (sub === 'level') {
    const lvl = parseInt(args[1], 10);
    if (isNaN(lvl) || lvl < 1) {
      return { logEntries: [makeEntry('Usage: debug level <number>', 'error')], statePatch: {} };
    }
    const newXpToNext = getPlayerXpToNextLevel(lvl);
    return {
      logEntries: [makeEntry(`Player level set to ${lvl}.`, 'system')],
      statePatch: {
        playerStats: { ...state.playerStats, level: lvl, xp: 0, xpToNext: newXpToNext },
      },
    };
  }

  if (sub === 'skill') {
    const skillId = args[1]?.toLowerCase();
    const lvl = parseInt(args[2], 10);
    const validSkills = ['fishing', 'bartering', 'business', 'luck'];
    if (!skillId || !validSkills.includes(skillId) || isNaN(lvl) || lvl < 1) {
      return { logEntries: [makeEntry('Usage: debug skill <fishing|bartering|business|luck> <level>', 'error')], statePatch: {} };
    }
    return {
      logEntries: [makeEntry(`${skillId} set to level ${lvl}.`, 'system')],
      statePatch: {
        playerStats: {
          ...state.playerStats,
          skills: { ...state.playerStats.skills, [skillId]: { level: lvl } },
        },
      },
    };
  }

  return {
    logEntries: [makeEntry(`Unknown debug subcommand: ${sub}. Try coins, skill, or level.`, 'error')],
    statePatch: {},
  };
}

export function exportSave(state: GameState): CommandResult {
  const json = JSON.stringify(state);
  const encoded = btoa(unescape(encodeURIComponent(json)));
  return {
    logEntries: [makeEntry('Save exported. Copy the code below:', 'system'), makeEntry(encoded, 'info')],
    statePatch: {},
  };
}

export function importSave(_state: GameState, args: string[]): CommandResult {
  if (args.length === 0) {
    return { logEntries: [makeEntry('Usage: import <base64-code>', 'error')], statePatch: {} };
  }

  let decoded: string;
  try {
    decoded = atob(args[0]);
  } catch {
    return { logEntries: [makeEntry('Invalid save code. Could not decode.', 'error')], statePatch: {} };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(decoded);
  } catch {
    return { logEntries: [makeEntry('Invalid save data. Could not parse JSON.', 'error')], statePatch: {} };
  }

  const validated = validateGameState(parsed);
  if (!validated) {
    return { logEntries: [makeEntry('Invalid save data. Validation failed.', 'error')], statePatch: {} };
  }

  return {
    logEntries: [makeEntry('Save imported successfully!', 'success')],
    statePatch: validated as Partial<GameState>,
  };
}
